import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { RowanTerm } from "@/components/RowanTerm";
import { CircleNotch, FileText, CalendarCheck, Repeat, CreditCard, FolderSimple, ArrowRight } from "@phosphor-icons/react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_COLOR = { paid: "var(--bk-success)", overdue: "var(--bk-energy)", sent: "var(--bk-accent)", draft: "var(--bk-text-2)", partial: "#B08840", Completed: "var(--bk-success)", "No-show": "#B08840", Cancelled: "var(--bk-text-2)", Scheduled: "var(--bk-accent)", active: "var(--bk-success)" };

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl p-3" style={{ background: "#F8F7F4" }}>
      <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>{label}</p>
      <p className="font-display text-lg font-bold mt-0.5" style={{ color: "var(--bk-text)" }} data-testid={testid}>{value}</p>
    </div>
  );
}

function Section({ Icon, title, count, children, testid, empty, action }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--bk-accent)" }}><Icon size={14} weight="fill" /> {title} <span className="opacity-60">{count}</span></h4>
        {action}
      </div>
      {count === 0 ? <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>{empty}</p> : <div className="space-y-1.5">{children}</div>}
    </section>
  );
}

const Row = ({ left, sub, right, status, testid }) => (
  <div className="flex items-center gap-3 rounded-lg border px-3 py-2" style={{ borderColor: "var(--bk-border)" }} data-testid={testid}>
    <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate" style={{ color: "var(--bk-text)" }}>{left}</p>{sub && <p className="text-xs truncate" style={{ color: "var(--bk-text-2)" }}>{sub}</p>}</div>
    {right && <span className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{right}</span>}
    {status && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize" style={{ background: "#F3F1EC", color: STATUS_COLOR[status] || "var(--bk-text-2)" }}>{status}</span>}
  </div>
);

// One place for everything about a customer: invoices, payments, bookings, packages and documents.
export function CustomerProfile({ businessId, customer, onClose, onGoToView }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!customer) return;
    setP(null);
    const q = new URLSearchParams({ name: customer.name || "", email: customer.email || "" });
    api.get(`/businesses/${businessId}/customers/profile?${q}`).then((r) => setP(r.data)).catch(() => setP({ error: true }));
  }, [businessId, customer]);

  const go = (view) => { onClose(); onGoToView?.(view); };
  const link = (view, label) => <button onClick={() => go(view)} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--bk-accent)" }}>{label} <ArrowRight size={11} weight="bold" /></button>;

  return (
    <Sheet open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="customer-profile">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl" style={{ color: "var(--bk-text)" }} data-testid="customer-profile-name">{customer?.name || "Customer"}</SheetTitle>
          <SheetDescription>{[p?.email || customer?.email, p?.phone || customer?.phone].filter(Boolean).join(" · ") || "No contact details yet"}</SheetDescription>
        </SheetHeader>
        {!p ? (
          <p className="text-sm inline-flex items-center gap-2 mt-6" style={{ color: "var(--bk-text-2)" }} data-testid="customer-profile-loading"><CircleNotch size={16} className="animate-spin" /> Pulling everything together…</p>
        ) : p.error ? (
          <p className="text-sm mt-6" style={{ color: "var(--bk-energy)" }}>Couldn't load this customer.</p>
        ) : (
          <div className="mt-5 space-y-6">
            {p.notes && <p className="text-sm italic rounded-lg p-3" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-text)" }}>{p.notes}</p>}
            <div className="grid grid-cols-3 gap-2" data-testid="customer-profile-stats">
              <Stat label="Owes you" value={p.summary.open_balance > 0 ? money(p.summary.open_balance) : "Paid up"} testid="profile-open-balance" />
              <Stat label="Lifetime paid" value={money(p.summary.lifetime_value)} testid="profile-lifetime" />
              <Stat label="Upcoming" value={p.summary.upcoming_count} testid="profile-upcoming" />
              <Stat label="Last visit" value={p.summary.last_visit || "—"} testid="profile-last-visit" />
              <Stat label="No-shows" value={p.summary.no_shows} testid="profile-no-shows" />
              <Stat label="Active packages" value={p.summary.active_credits} testid="profile-active-credits" />
            </div>

            <Section Icon={FileText} title="Invoices" count={p.invoices.length} testid="profile-invoices" empty="No invoices yet." action={link("invoices", "New invoice")}>
              {p.invoices.map((i) => <Row key={i.id} testid={`profile-invoice-${i.id}`} left={i.number ? `Invoice ${i.number}` : "Invoice"} sub={[i.date, i.due_date ? `due ${i.due_date}` : ""].filter(Boolean).join(" · ")} right={money(i.balance_due ?? i.total)} status={i.status} />)}
            </Section>

            <Section Icon={CreditCard} title="Payments" count={p.payments.length} testid="profile-payments" empty="No payments recorded.">
              {p.payments.map((x) => <Row key={x.id} left={x.note || "Payment"} sub={[x.date, x.account].filter(Boolean).join(" · ")} right={money(x.amount)} />)}
            </Section>

            <Section Icon={CalendarCheck} title="Bookings" count={p.bookings.length} testid="profile-bookings" empty="No appointments on record." action={link("bookings", "All bookings")}>
              {p.bookings.slice(0, 12).map((b) => <Row key={b.id} left={b.service || "Booking"} sub={[b.date, [b.start_time, b.end_time].filter(Boolean).join("–"), b.status === "No-show" ? (b.session_deducted ? "session deducted" : "no deduction") : ""].filter(Boolean).join(" · ")} status={b.status} />)}
            </Section>

            <Section Icon={Repeat} title={<RowanTerm term="unearned_revenue">Packages & memberships</RowanTerm>} count={p.credits.length} testid="profile-credits" empty="No packages, memberships or gift cards." action={link("members", "Manage")}>
              {p.credits.map((c) => <Row key={c.id} left={c.item_name || c.kind} sub={[c.purchased_at, c.expires_at ? `expires ${c.expires_at}` : ""].filter(Boolean).join(" · ")} right={c.gift_card_balance != null ? `${money(c.gift_card_balance)} left` : c.remaining != null ? `${c.remaining}/${c.sessions_included} left` : money(c.price_paid)} status={c.status} />)}
            </Section>

            <Section Icon={FolderSimple} title="Documents" count={p.documents.length} testid="profile-documents" empty="No Vault documents mention this customer. Add their name in 'Connected to' when filing one." action={link("vault", "Open Vault")}>
              {p.documents.map((d) => <Row key={d.id} left={d.title} sub={[d.folder, d.expires_on ? `expires ${d.expires_on}` : ""].filter(Boolean).join(" · ")} />)}
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
